"use client"

import React, { useActionState, useEffect, useState } from "react"
import Form from "next/form"
import { useRouter } from "next/navigation"
import { updatePasswordAction } from "@/actions/profileActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckCircledIcon, CrossCircledIcon } from "@radix-ui/react-icons"
import { LoaderCircle, PlusIcon, SaveIcon } from "lucide-react"
import { useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { UpdatePasswordSchema } from "@/components/account/profile/profile"
import { FormRow } from "@/components/account/shared/FormRow"
import PasswordChecklist from "@/components/common/PasswordChecklist"
import { UbAccountProfilePasswordViewStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

const resolver = zodResolver(UpdatePasswordSchema)

interface AccountProfilePasswordProps {
  accountContext: AccountContext
  blok: UbAccountProfilePasswordViewStoryblok
}

export default function AccountProfilePasswordClient(props: AccountProfilePasswordProps) {
  const { setProfileUpdated } = useUbStorefrontState()
  const router = useRouter()

  const [password, setPassword] = useState("")

  const [passwordChecklistValidIconColor, setPasswordChecklistValidIconColor] = useState("text-gray-400")
  const [passwordChecklistInvalidIconColor, setPasswordChecklistInvalidIconColor] = useState("text-gray-400")

  useEffect(() => {
    setPasswordChecklistValidIconColor(password ? "text-green-500" : "text-gray-400")
    setPasswordChecklistInvalidIconColor(password ? "text-red-500" : "text-gray-400")
  }, [password])

  async function action(initialState: any, formData: FormData) {
    return updatePasswordAction(initialState, formData, props.accountContext.customerData?.customerType).then(
      async (state) => {
        if (!hasEntries(state.errors)) {
          setProfileUpdated(true)
          router.push(getLinkWithPrefixFromURLAndLocale("/account/profile", props.accountContext.sbLocale))
        }
        return {
          values: state.values,
          errors: state.errors,
        }
      }
    )
  }

  const [state, formAction, isPending] = useActionState(action, {
    values: {
      currentPassword: "",
      password: "",
      confirmPassword: "",
    },
    errors: {},
  })

  const { formState, register, reset } = useForm({
    values: state.values,
    errors: state.errors,
    resetOptions: {
      keepErrors: true,
    },
    mode: "onChange",
    resolver,
  })

  useEffect(() => {
    reset(state.values, {
      keepErrors: true,
    })
  }, [reset, state.values])

  return (
    <>
      <AccountBackLinkClient label={props.blok.label_common_back} />
      <div className={"relative flex w-full flex-col gap-2 lg:gap-6"}>
        <div className={"flex flex-col gap-2"}>
          <h2 className={"text-lg font-bold"}>{props.blok.title}</h2>
          <p>{props.blok.description}</p>
          <p className={"text-xs"}>{props.blok.required}</p>
        </div>
        <div>
          <Form action={formAction}>
            <div className={"flex flex-col gap-2 lg:gap-6"}>
              <FormRow label={props.blok.current_password} htmlFor={"currentPassword"} required>
                <PasswordToggleInput
                  autoComplete="current-password"
                  {...register("currentPassword")}
                  className={cn("", { "border-primary": formState.errors.currentPassword })}
                />
                {formState.errors.currentPassword && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {props.blok.current_password_invalid}
                  </p>
                )}
              </FormRow>

              <FormRow label={props.blok.password} htmlFor={"password"} required>
                <PasswordToggleInput
                  {...register("password")}
                  className={cn("", { "border-primary": formState.errors.password })}
                  autoComplete={"new-password"}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {formState.errors.password && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {props.blok.password_invalid}
                  </p>
                )}
                <PasswordChecklist
                  className={"mt-1 flex flex-wrap items-center justify-start gap-2 text-xs"}
                  itemClassName={"flex gap-1 items-center pt-0"}
                  rules={["minLength", "lowercase", "capital", "specialChar", "number"]}
                  value={password}
                  minLength={10}
                  messages={{
                    minLength: props.blok.password_checklist_minlength,
                    lowercase: props.blok.password_checklist_lowercase,
                    capital: props.blok.password_checklist_capital,
                    specialChar: props.blok.password_checklist_specialchar,
                    number: props.blok.password_checklist_number,
                  }}
                  iconSize={16}
                  iconComponents={{
                    ValidIcon: <CheckCircledIcon className={passwordChecklistValidIconColor} />,
                    InvalidIcon: <CrossCircledIcon className={passwordChecklistInvalidIconColor} />,
                  }}
                />
              </FormRow>

              <FormRow label={props.blok.confirm_password} htmlFor={"confirmPassword"} required>
                <PasswordToggleInput
                  {...register("confirmPassword")}
                  className={cn("", { "border-primary": formState.errors.confirmPassword })}
                  autoComplete={"new-password"}
                />
                {formState.errors.confirmPassword && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {formState.errors.confirmPassword.message === "notMatch"
                      ? props.blok.password_not_match
                      : props.blok.password_invalid}
                  </p>
                )}
              </FormRow>
              <div className="flex flex-wrap justify-between gap-2 lg:justify-end">
                <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending} className={"gap-1"}>
                  {!isPending ? (
                    <SaveIcon className={"inline size-5"} />
                  ) : (
                    <LoaderCircle className={"inline size-5 animate-spin"} />
                  )}
                  <span>{props.blok.submit}</span>
                </Button>

                <Button
                  type={"reset"}
                  variant="outline"
                  size={"defaultNoGrow"}
                  className={"gap-1"}
                  onClick={() => router.back()}
                >
                  <PlusIcon className={"inline size-5 rotate-45"} />
                  <span>{props.blok.cancel}</span>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        {props.blok.help && (
          <div>
            <UbRichText blok={{ content: props.blok.help, _uid: "", component: "ub_rich_text" }} />
          </div>
        )}
      </div>
    </>
  )
}
